import { useState, useEffect, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { supabase } from '../lib/supabase';

export const useFeatures = () => {
  const [features, setFeatures] = useState<any[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { setValue, getValues } = useFormContext();

  // Fetch features once on mount
  useEffect(() => {
    fetchFeatures();
  }, []);

  // Initialize selectedFeatures from form values only once
  useEffect(() => {
    const formFeatures = getValues('features');
    if (Array.isArray(formFeatures) && formFeatures.length > 0) {
      setSelectedFeatures(formFeatures);
    }
  }, []); // Empty dependency array to run only once

  // Update form values when selectedFeatures change, but use a callback ref pattern to avoid re-rendering
  const updateFormValues = useCallback((features: string[]) => {
    // Use setTimeout to push the update to the next tick, avoiding focus loss
    setTimeout(() => {
      setValue('features', features, { shouldDirty: true });
    }, 0);
  }, [setValue]);

  // Custom setter that both updates state and form values without causing re-renders
  const setFeatureSelection = useCallback((features: string[]) => {
    setSelectedFeatures(features);
    updateFormValues(features);
  }, [updateFormValues]);

  const fetchFeatures = async () => {
    try {
      const { data, error } = await supabase
        .from('features')
        .select('*')
        .order('category', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      
      setFeatures(data || []);
    } catch (err: any) {
      console.error('Error fetching features:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    features,
    selectedFeatures,
    setSelectedFeatures: setFeatureSelection,
    loading,
    error
  };
};